import { useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ArrowUpRight } from 'lucide-react';
import { DubaiClock } from './DubaiClock';
import { EASE } from './Reveal';

const LINKS = [
  { to: '/', label: 'Home', testid: 'nav-link-home' },
  { to: '/about', label: 'About', testid: 'nav-link-about' },
  { to: '/services', label: 'Services', testid: 'nav-link-services' },
  { to: '/work', label: 'Work', testid: 'nav-link-work' },
  { to: '/contact', label: 'Contact', testid: 'nav-link-contact' },
];

export const Nav = () => {
  const [open, setOpen] = useState(false);

  return (
    <header className="fixed top-0 inset-x-0 z-50 bg-cream/85 backdrop-blur-md border-b border-ink/10">
      <div className="px-6 sm:px-12 h-[76px] flex items-center justify-between gap-6">
        <Link to="/" data-testid="nav-brand-logo" onClick={() => setOpen(false)} className="shrink-0">
          <img src="/assets/logo-ink.png" alt="Butter Media" className="h-8 sm:h-9 w-auto" />
        </Link>

        <nav className="hidden lg:flex items-center gap-1.5">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.testid}
              className={({ isActive }) =>
                `px-4 py-2 rounded-full text-sm font-medium tracking-wide transition-colors duration-300 ${
                  isActive ? 'bg-ink text-cream' : 'text-ink/65 hover:text-ink hover:bg-ink/5'
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-5">
          <div className="hidden xl:block">
            <DubaiClock />
          </div>
          <Link
            to="/contact"
            data-testid="nav-cta-start-project"
            className="hidden sm:inline-flex group items-center gap-2 bg-butter text-ink font-slab font-bold text-sm px-6 py-3 rounded-full border border-ink shadow-[3px_3px_0_#1A1A1A] hover:shadow-none hover:translate-x-[3px] hover:translate-y-[3px] transition-all duration-300"
          >
            Start a Project
            <ArrowUpRight size={16} className="group-hover:rotate-45 transition-transform duration-300" />
          </Link>
          <button
            data-testid="nav-mobile-toggle"
            className="lg:hidden p-2 text-ink"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: EASE }}
            className="lg:hidden overflow-hidden border-t border-ink/10 bg-cream"
          >
            <div className="px-6 py-6 flex flex-col">
              {LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-${l.testid}`}
                  className={({ isActive }) =>
                    `font-slab font-bold text-3xl py-3.5 border-b border-ink/10 ${isActive ? 'text-royal' : 'text-ink'}`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <div className="pt-6 pb-2 flex items-center justify-between">
                <DubaiClock />
                <Link
                  to="/contact"
                  onClick={() => setOpen(false)}
                  data-testid="mobile-nav-cta"
                  className="inline-flex items-center gap-2 bg-butter text-ink font-slab font-bold text-sm px-5 py-2.5 rounded-full border border-ink"
                >
                  Start a Project <ArrowUpRight size={15} />
                </Link>
              </div>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
